import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import styled, { keyframes } from "styled-components";
import Seo from "./Seo";
import { matchCuratedWallets } from "./curatedWalletData";
import { netMovedSats } from "./txValue";
import { satsToBtc, truncateHash, btcToString } from "./utils";
import { PageContainer } from "./sharedStyleds";
import { Link } from "react-router";
import TransactionTape from "./TransactionTape";
import KnownWalletActivity from "./KnownWalletActivity";
import RecentBlocks from "./RecentBlocks";
import WhaleWatch, { WHALE_THRESHOLDS, exchangeFlowOf } from "./WhaleWatch";
import { getBtcPrice } from "./getBtcPrice";
import { routeMeta } from "./routeMeta";
import { getRecentBlocks, getBlockTxids } from "./mempoolApi";

// recharts is 105KB gzipped - the single heaviest thing the front page pulls,
// and more than the rest of the bundle put together once chart.js left. It draws
// one chart that sits below the tape, both panels, the block list and the stats,
// so nobody sees it until they scroll. Deferring it means the page above the
// fold renders on 119KB instead of 293KB and the chart arrives behind it.
//
// No layout shift to pay for: ChartFrame has a fixed height, so the space is
// reserved whether the chart has loaded or not.
const TransactionFlowChart = lazy(() => import("./TransactionFlowChart"));

// covers the 5 minute chart window and the tape with room to spare
const MAX_TRANSACTIONS = 2000;

// an hour of history, which is enough to see the confirmation rhythm
const MAX_BLOCKS = 6;

// Whale transfers are kept separately from the transaction buffer, which holds
// about five minutes. A transfer over 10 BTC is uncommon enough that a five
// minute window would usually show none, so these accumulate for the session.
const MAX_WHALES = 25;
const WHALE_FLOOR = WHALE_THRESHOLDS[0];

// the panel is priced in dollars as well as BTC; the rate moves slowly enough
const PRICE_REFRESH_MS = 300000;

// the websocket announces a block the moment it is found, but mempool.space
// needs a beat to index it before the pool name and fee total are available
const BLOCK_INDEX_DELAY_MS = 4000;

// a fallback for the blocks_sub subscription dropping a message; a block only
// arrives every ten minutes, so this is cheap
const BLOCK_POLL_MS = 60000;

// the feed drops out often enough that reconnecting matters; the delay is only
// there to avoid hammering the host through an outage. It doubles on each
// failed attempt, so a connection that is down for an hour costs a try every
// thirty seconds rather than one a second.
const RECONNECT_MS = 1000;
const RECONNECT_MAX_MS = 30000;

// A socket can die without closing. A tablet that sleeps, or a network that
// drops underneath it, leaves one that reads as open and never delivers again,
// and nothing fires to say so. The feed sends several transactions a second,
// so a minute of silence means the connection is gone, not that the chain is
// quiet.
const STALL_MS = 60000;

// Hashes already handled, so a transaction the feed sends twice is counted
// once. Bounded because the page can stay open for days; at the feed's rate of
// about four a second this covers the last three hours or so.
const SEEN_HASHES = 50000;

// most transactions are dust, so the interesting ones are easy to miss
const SIZE_FILTERS = [
  { label: "All", min: 0 },
  { label: "> 1", min: 1 },
  { label: "> 10", min: 10 },
  { label: "> 50", min: 50 },
];

const fmtSince = (date) => {
  if (!date) return "—";
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  const mins = Math.floor(seconds / 60);
  return mins > 0 ? `${mins}m ${seconds % 60}s ago` : `${seconds}s ago`;
};

const BtcLivePage = PageContainer;

const Header = styled.h1`
  /* An h1 for semantics - no page had one - sized to the h2 it replaces, so
     nothing moves. terminal.css puts h1 at 30px, which would be a jump. */
  font-size: 1.5rem;
  line-height: 1.4em;
  /* the gap under the nav belongs to PageContainer, not to the heading */
  margin-top: 0;
  margin-bottom: 2px;
`;

// a grid rather than wrapping flex, so the cards never leave one orphaned on
// its own row on narrow screens
const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem 1rem;
  margin-bottom: 0.8rem;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const StatCard = styled.div`
  min-width: 0;
`;

const EmptyTd = styled.td`
  text-align: center;
  color: #a3abba;
  padding: 1rem 0.5rem;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: #a3abba;
  margin-bottom: 0.3rem;
`;

const FilterButton = styled.button`
  padding: 0 0.45rem;
  font-size: 0.75rem;
  background-color: ${({ $active }) => ($active ? "#3f3f44" : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#a3abba")};
  border: 1px solid #3f3f44;
  border-radius: 3px;
  cursor: pointer;
`;

// shorter on phones so the transaction feed is not pushed off the screen
const ChartFrame = styled.div`
  height: 190px;
  margin-bottom: 1rem;

  @media (max-width: 700px) {
    height: 130px;
  }
`;

const StatLabel = styled.p`
  margin: 0;
  font-size: 0.7rem;
  color: #a3abba;
`;

const StatValue = styled.p`
  margin: 0;
  font-size: 0.95rem;
  overflow-wrap: anywhere;
`;

// grey while the feed is down, so a page that has lost its connection says so
// instead of quietly showing the last thing it heard
const ConnectStatus = styled.span`
  font-size: 0.8rem;
  color: ${({ $off }) => ($off ? "#a3abba" : "#0eed0e")};
  vertical-align: middle;

  &::before {
    content: "";
    display: inline-block;
    width: 7px;
    height: 7px;
    margin-right: 0.35rem;
    border-radius: 50%;
    background-color: ${({ $off }) => ($off ? "#a3abba" : "#0eed0e")};
  }
`;

const arrive = keyframes`
  from { background-color: rgba(255, 153, 0, 0.22); }
  to   { background-color: transparent; }
`;

const TxTable = styled.table`
  font-size: 12px;
`;

const Faq = styled.section`
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px dotted #3f3f44;
  max-width: 75ch;
`;

const FaqTitle = styled.h3`
  margin-bottom: 0.2rem;
  font-size: 1rem;
`;

const Question = styled.h4`
  margin: 1rem 0 0.2rem;
  font-size: 0.95rem;
`;

const Answer = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: #a3abba;
`;

const DesktopTh = styled.th`
  @media (max-width: 700px) {
    display: none;
  }
`;

const DesktopTd = styled.td`
  @media (max-width: 700px) {
    display: none;
  }
`;

// rows touching a known wallet get a left edge and a slight lift, so they are
// findable while the feed scrolls past
const TxRow = styled.tr`
  animation: ${arrive} 1.2s ease-out;

  ${({ $tagged }) =>
    $tagged &&
    `
    background-color: rgba(50, 161, 206, 0.09);
    box-shadow: inset 3px 0 0 #32a1ce;
  `}
`;

// a confirmed transaction keeps its place in the tape rather than vanishing,
// so you can watch one you were following actually settle
const ConfirmedTag = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0 0.35rem;
  border: 1px solid #0eed0e;
  border-radius: 3px;
  font-size: 0.7rem;
  white-space: nowrap;
  color: #0eed0e;
`;

const WalletTag = styled(Link)`
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0 0.35rem;
  border: 1px solid #32a1ce;
  border-radius: 3px;
  font-size: 0.7rem;
  white-space: nowrap;
  text-decoration: none;

  &:hover {
    background-color: #32a1ce;
    color: #222225;
  }
`;

const BtcLive = () => {
  // Docs: https://www.blockchain.com/explorer/api/api_websocket
  const webSocket = useRef(null);
  // set while tearing a socket down on purpose, so its own close handler knows
  // not to treat it as a dropped connection and reconnect
  const closingOnPurpose = useRef(false);
  const reconnectTimer = useRef(null);
  const reconnectAttempts = useRef(0);
  // when the socket last showed any sign of life, for the stall check
  const lastActivity = useRef(Date.now());
  // whether this page has been connected before, so a dropped feed can say
  // "reconnecting" rather than looking like a first load
  const [wasConnected, setWasConnected] = useState(false);
  const seenHashes = useRef(new Set());
  const [transactions, setTransactions] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [totalBtc, setTotalBtc] = useState(0);
  // counted separately from the transactions array, which is capped for memory
  // and so stops growing once it is full
  const [seenCount, setSeenCount] = useState(0);
  const [lastBlockAt, setLastBlockAt] = useState(null);
  // how many transactions this page had already seen, per block height
  const [confirmations, setConfirmations] = useState({});
  // the block matcher reads the current transactions without re-running on
  // every one that arrives, which is several a second
  const transactionsRef = useRef([]);
  const tipHeight = useRef(null);
  const live = useRef(true);
  // the socket handler is set up once on mount and outlives every render, so it
  // reaches the block loader through a ref rather than closing over it
  const loadBlocksRef = useRef(() => {});
  const loading = useRef(false);
  const [largestTx, setLargestTx] = useState(null);

  // The last few blocks, so the two panels below start with something in them.
  // Kept apart from `transactions` on purpose: that array is this session's live
  // feed and drives the tape, the five minute volume chart and the session
  // totals, all of which would start out lying if they were handed transactions
  // that were mined before the page opened. Only the whale and known-wallet
  // panels read this, because only they are about events rather than about the
  // session.
  const [seeded, setSeeded] = useState([]);
  //
  // The stats row below stays session-scoped and says so in its labels. It sits
  // directly under a panel that now shows the last few blocks, so "Largest seen
  // 2.81 BTC" beneath a listed 444 BTC transfer read as a contradiction until
  // the labels named the window they count.
  const [minBtc, setMinBtc] = useState(0);
  const [whales, setWhales] = useState([]);
  const [whaleMin, setWhaleMin] = useState(WHALE_THRESHOLDS[1]);
  // cumulative for the session, not derived from the capped buffer
  const [flow, setFlow] = useState({ inBtc: 0, outBtc: 0 });
  const [btcPrice, setBtcPrice] = useState(0);
  // a block confirms transactions that arrived minutes ago, so they have long
  // since fallen out of the newest 100 the table shows. Without this you can
  // read that a block confirmed 1,500 of them and never see one.
  const [confirmedOnly, setConfirmedOnly] = useState(false);
  // Most broadcasts move nothing: a wallet paying only itself nets to zero, and
  // one address looping single-input self-sends alongside a zero-value OP_RETURN
  // - how data gets written to the chain - filled 48 of the last 100 rows with
  // "0.00000000 BTC". The tape has drawn transfers only for this reason since it
  // was written; the table was the one place left showing the raw feed, and on
  // the front page it read as a broken column rather than a busy chain.
  //
  // A toggle rather than a filter on arrival: they are real transactions, the
  // caption says they are being held back, and one click shows them. The
  // counters above are untouched - "transactions seen" counts what the socket
  // sent, and "BTC moved" was already net, so a self-send adds nothing to it.
  const [movedOnly, setMovedOnly] = useState(true);
  // ticks once a second purely so "time since last block" keeps counting up
  const [, setClock] = useState(0);

  useEffect(() => {
    transactionsRef.current = transactions;
  }, [transactions]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const price = await getBtcPrice();
      if (!cancelled && price) setBtcPrice(price);
    };
    load();
    const id = setInterval(load, PRICE_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Fetched once, on mount. /api/recent-activity sweeps the last few blocks
  // server-side and is CDN cached, so this costs a few KB rather than the several
  // megabytes reading whole blocks in the browser would.
  //
  // Failure is silent and harmless: the panels simply start empty, which is
  // exactly where they were before this existed. A seeding request is not worth
  // an error message on a page whose actual content is arriving over a socket.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/recent-activity")
      .then((response) => (response.ok ? response.json() : []))
      .then((items) => {
        if (cancelled || !Array.isArray(items)) return;
        setSeeded(
          // `seeded` marks these as read from a mined block rather than seen
          // arriving, so the panels can name the block instead of a clock time
          // that every transaction in that block shares.
          items.map((item) => ({
            ...item,
            time: new Date(item.time * 1000),
            seeded: true,
          }))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setClock((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Reads the recent blocks, and works out what each new one confirmed.
  //
  // blocks_sub only reports blocks found from now on, so the list is seeded from
  // mempool.space instead - otherwise the stat reads "-" for most of a visit and
  // the section opens empty.
  //
  // mempool.space rather than blockchain.info/latestblock because that endpoint
  // returns no Access-Control-Allow-Origin on GET, so the browser blocks it.
  // blockchain.info's multiaddr and rawtx endpoints do send it, which is why the
  // explorer works and this would not have.
  const loadBlocks = useCallback(async () => {
    // the 60 second poll and the websocket's own refresh can land together, and
    // a block's transaction ids are a 400KB response worth fetching once
    if (loading.current) return;
    loading.current = true;
    try {
      await readBlocks();
    } finally {
      loading.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readBlocks = async () => {
    const latest = await getRecentBlocks();
    if (!latest || latest.length === 0 || !live.current) return;

    setBlocks(latest.slice(0, MAX_BLOCKS));
    if (latest[0]?.timestamp) setLastBlockAt(new Date(latest[0].timestamp * 1000));

    // First load seeds the list and nothing more: these blocks were mined
    // before the page opened, so there is nothing on screen they could have
    // confirmed and no reason to pull their transaction ids.
    const previousHeight = tipHeight.current;
    tipHeight.current = latest[0].height;
    if (previousHeight === null) return;

    const fresh = latest
      .filter((block) => block.height > previousHeight)
      .reverse();
    for (const block of fresh) {
      await matchBlock(block);
    }
  };

  // Which of the transactions still on screen did this block confirm?
  //
  // Reading the current transactions from a ref rather than from a state
  // updater keeps the match pure: StrictMode invokes updaters twice, so
  // counting inside one would double every figure.
  const matchBlock = async (block) => {
    if (transactionsRef.current.length === 0) return;

    const txids = await getBlockTxids(block.id);
    if (!txids || !live.current) return;

    const inBlock = new Set(txids);
    const matched = new Set(
      transactionsRef.current
        .filter((tx) => !tx.confirmed && inBlock.has(tx.hash))
        .map((tx) => tx.hash)
    );

    if (matched.size > 0) {
      setTransactions((prev) =>
        prev.map((tx) =>
          matched.has(tx.hash)
            ? { ...tx, confirmed: true, confirmedHeight: block.height }
            : tx
        )
      );
    }
    setConfirmations((prev) => ({ ...prev, [block.height]: matched.size }));
  };

  useEffect(() => {
    loadBlocksRef.current = loadBlocks;
  }, [loadBlocks]);

  useEffect(() => {
    live.current = true;
    loadBlocks();
    const id = setInterval(loadBlocks, BLOCK_POLL_MS);
    return () => {
      live.current = false;
      clearInterval(id);
    };
  }, [loadBlocks]);

  useEffect(() => {
    // document.title = "d2index | BTC Live";

    // connect when component mounts
    startSocket();

    // disconnect when component is unmounted
    return stopSocket;
    // Must run exactly once on mount. startSocket is also bound to the reconnect
    // button, so it cannot move inside the effect, and it is rebuilt every render
    // - listing it as a dependency would reopen the socket continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps a page that is left open alive. Three things end a feed without
  // closing it: a socket that goes silent, a device waking from sleep with its
  // connection long gone, and the network coming back while a reconnect waits
  // out its backoff. Each of them gets a fresh socket.
  useEffect(() => {
    const quiet = (ms) => Date.now() - lastActivity.current > ms;
    const restart = () => {
      startSocket();
      // blocks found while the page slept are matched against the tape here
      // rather than on the next minute's poll
      loadBlocksRef.current();
    };

    const id = setInterval(() => {
      // a scheduled reconnect is already handling it
      if (reconnectTimer.current) return;
      if (quiet(STALL_MS)) restart();
    }, STALL_MS / 4);

    // Waking is the moment to check rather than waiting for the interval: a
    // sleeping tab's timers are paused, and the feed should be live by the
    // time anyone is looking at it.
    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      const socket = webSocket.current;
      // one still connecting is left to finish; the interval catches it if it
      // never does
      if (socket && socket.readyState === WebSocket.CONNECTING) return;
      if (!socket || socket.readyState !== WebSocket.OPEN || quiet(5000)) {
        restart();
      }
    };
    const onOnline = () => restart();

    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("online", onOnline);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("online", onOnline);
    };
    // startSocket reaches everything through refs and state setters, so the
    // copy from the first render behaves the same as any later one
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startSocket = () => {
    // a socket can still be open here - a reconnect racing a manual restart, or
    // React remounting in development - and leaving it open delivers every
    // transaction twice, which double counts the stats
    stopSocket();

    closingOnPurpose.current = false;
    lastActivity.current = Date.now();
    webSocket.current = new WebSocket("wss://ws.blockchain.info/inv");

    webSocket.current.onopen = () => {
      setStartDate(new Date());
      setWasConnected(true);

      const unconfirmedSub = {
        op: "unconfirmed_sub",
      };
      const blocksSub = {
        op: "blocks_sub",
      };

      webSocket.current.send(JSON.stringify(unconfirmedSub));
      webSocket.current.send(JSON.stringify(blocksSub));
    };

    // should newest items go first in the array?
    webSocket.current.onmessage = (evt) => {
      lastActivity.current = Date.now();
      reconnectAttempts.current = 0;
      const message = JSON.parse(evt.data);
      if (message.op === "utx") {
        // The feed can send a transaction it has already sent - a page left
        // open overnight showed one 324 BTC transfer four times in the whale
        // panel. Beyond the double counting, every list on the page keys its
        // rows by hash, and React cannot reconcile two rows with one key: the
        // tape kept the orphans and grew to hundreds of bars against its 90.
        const seen = seenHashes.current;
        if (seen.has(message.x.hash)) return;
        seen.add(message.x.hash);
        // a Set iterates in insertion order, so the first value is the oldest
        if (seen.size > SEEN_HASHES) seen.delete(seen.values().next().value);

        // console.log("Unconfirmed TX:", message.x);
        // What actually left the sending addresses, rather than the total of the
        // outputs. Bitcoin has no change, so the remainder of a spend comes back
        // to the sender as another output and an output total counts it as
        // movement. Measured over 28,000 transactions it applies to 85% of them
        // and inflates the total value roughly sixteen times over - the same
        // distance blockchain.com's output-volume sits above its
        // estimated-transaction-volume, which is this correction by another
        // name. src/txValue.js is shared with the whale alert bot so the
        // page and the bot cannot disagree.
        const netMovedBtc = satsToBtc(netMovedSats(message.x));
        const fixed = {
          ...message.x,
          time: new Date(message.x.time * 1000),
          netMovedBtc,
          // which curated wallets, if any, this transaction touches
          tags: matchCuratedWallets(message.x),
        };
        // which side of an exchange this landed on, if either
        fixed.flow = exchangeFlowOf(fixed.tags);
        setTransactions((prevArray) => {
          const newArr = [fixed, ...prevArray];
          // enough to cover the 5 minute chart window and the tape; the table
          // only ever shows 100. This used to keep 100,000.
          if (newArr.length > MAX_TRANSACTIONS) newArr.pop();
          return newArr;
        });
        setSeenCount((n) => n + 1);

        if (netMovedBtc >= WHALE_FLOOR) {
          setWhales((prev) => {
            // the list outlives the seen set above, which forgets after a few
            // hours, and a repeat here is the one that shows
            if (prev.some((tx) => tx.hash === fixed.hash)) return prev;
            const next = [fixed, ...prev];
            if (next.length > MAX_WHALES) next.pop();
            return next;
          });
        }

        // Counted once per transaction rather than once per tag. An exchange
        // moving coins between two of its own wallets shows up as both a send
        // and a receive, and is not flow in either direction.
        if (fixed.flow && fixed.flow.tone !== "internal") {
          const tone = fixed.flow.tone;
          // net for the same reason the rows above use it: this is a claim about
          // coins arriving at or leaving an exchange, and change returning to the
          // sender did neither
          setFlow((prev) => ({
            inBtc: prev.inBtc + (tone === "in" ? netMovedBtc : 0),
            outBtc: prev.outBtc + (tone === "out" ? netMovedBtc : 0),
          }));
        }
        setTotalBtc((prevTotal) => prevTotal + netMovedBtc);
        setLargestTx((prev) =>
          !prev || netMovedBtc > prev.netMovedBtc ? fixed : prev
        );
      } else if (message.op === "block") {
        // the payload here carries no pool name or fee total, and its txIndexes
        // cannot be matched to anything - tx_index on unconfirmed transactions
        // is deprecated and always 0. It is used purely as the signal to go and
        // read the block properly.
        setLastBlockAt(new Date());
        setTimeout(loadBlocksRef.current, BLOCK_INDEX_DELAY_MS);
      } else {
        // Not debug output: the feed sending an op this page does not handle is
        // the only signal that blockchain.info has changed something. It stays
        // quiet in normal operation, unlike the two logs that used to fire on
        // every connect and every search.
        console.warn("Bitcoin Live: unhandled socket message", message.op);
      }
    };

    // this was on the ref itself rather than on the socket, so it never fired
    webSocket.current.onerror = (err) => {
      console.error("Bitcoin Live socket error:", err);
    };

    webSocket.current.onclose = () => {
      setStartDate(null);
      if (closingOnPurpose.current) return;
      const delay = Math.min(
        RECONNECT_MS * 2 ** reconnectAttempts.current,
        RECONNECT_MAX_MS
      );
      reconnectAttempts.current += 1;
      reconnectTimer.current = setTimeout(startSocket, delay);
    };
  };

  // Closes the socket for good.
  //
  // This used to be `webSocket.current.close()` alone, which did the opposite of
  // what it looked like: close() fires onclose, and onclose reconnected a second
  // later. Leaving the page never stopped the feed, so every visit left another
  // socket running for the life of the page load, each one pushing the same
  // transactions into state again.
  //
  // Detaching the handlers before closing is what makes it final. A close
  // already scheduled cannot revive the socket, and a message in flight cannot
  // land in state after the component has gone.
  const stopSocket = () => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }

    const socket = webSocket.current;
    if (!socket) return;

    closingOnPurpose.current = true;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    socket.close();
    webSocket.current = null;
    setStartDate(null);
  };

  // filtering the table only - the tape and chart keep showing everything, so
  // the filter narrows what you read without hiding what is happening
  const visibleTransactions = transactions
    .filter((tx) => tx.netMovedBtc >= minBtc)
    .filter((tx) => !movedOnly || tx.netMovedBtc > 0)
    .filter((tx) => !confirmedOnly || tx.confirmed)
    .slice(0, 100);

  const confirmedCount = transactions.filter((tx) => tx.confirmed).length;

  // Live first, then the seeded blocks behind it, so anything arriving now sits
  // at the top and the panel fills in from history underneath. A transaction seen
  // live and then confirmed in a seeded block would appear twice, so the seeded
  // copy is dropped when the hash is already present.
  const liveHashes = new Set(transactions.map((tx) => tx.hash));
  const seededOnly = seeded.filter((tx) => !liveHashes.has(tx.hash));
  const seededWhales = [...whales, ...seededOnly].slice(0, MAX_WHALES);
  const seededTagged = [
    ...transactions.filter((tx) => tx.tags && tx.tags.length > 0),
    ...seededOnly.filter((tx) => tx.tags && tx.tags.length > 0),
  ];

  return (
    <BtcLivePage>
      <Seo {...routeMeta["/"]} path="/" />
      <Header>
        {routeMeta["/"].heading}{" "}
        {startDate ? (
          <ConnectStatus>connected</ConnectStatus>
        ) : (
          wasConnected && <ConnectStatus $off>reconnecting…</ConnectStatus>
        )}
      </Header>
      <p>{routeMeta["/"].intro}</p>
      <TransactionTape transactions={transactions} />

      <WhaleWatch
        whales={seededWhales}
        flow={flow}
        minBtc={whaleMin}
        setMinBtc={setWhaleMin}
        btcPrice={btcPrice}
      />

      <KnownWalletActivity transactions={seededTagged} />

      <RecentBlocks blocks={blocks} confirmations={confirmations} />

      <StatsContainer>
        {/* <StatCard>
          {startDate ? (
            <button className="btn btn-error btn-ghost" onClick={stopSocket}>
              Pause
            </button>
          ) : (
            <button className="btn btn-primary btn-ghost" onClick={startSocket}>
              Play
            </button>
          )}
        </StatCard> */}
        <StatCard>
          <StatLabel>Transactions seen</StatLabel>
          <StatValue>{seenCount.toLocaleString()}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>BTC moved this session</StatLabel>
          <StatValue>{btcToString(totalBtc)}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Largest this session</StatLabel>
          <StatValue>
            {largestTx ? (
              <Link to={`/btc-explorer/${largestTx.hash}`}>
                {btcToString(largestTx.netMovedBtc)} BTC
              </Link>
            ) : (
              "—"
            )}
          </StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>
            Last block{blocks[0] && ` #${blocks[0].height.toLocaleString()}`}
          </StatLabel>
          <StatValue>{fmtSince(lastBlockAt)}</StatValue>
        </StatCard>
      </StatsContainer>

      <ChartFrame>
        <Suspense fallback={null}>
          <TransactionFlowChart transactions={transactions} />
        </Suspense>
      </ChartFrame>

      <FilterBar>
        <span>Show transactions over</span>
        {SIZE_FILTERS.map((filter) => (
          <FilterButton
            key={filter.label}
            type="button"
            $active={minBtc === filter.min}
            onClick={() => setMinBtc(filter.min)}
          >
            {filter.label}
            {filter.min > 0 && " BTC"}
          </FilterButton>
        ))}
        <FilterButton
          type="button"
          $active={movedOnly}
          onClick={() => setMovedOnly((on) => !on)}
        >
          Moved something
        </FilterButton>
        <FilterButton
          type="button"
          $active={confirmedOnly}
          onClick={() => setConfirmedOnly((on) => !on)}
        >
          Confirmed{confirmedCount > 0 && ` (${confirmedCount.toLocaleString()})`}
        </FilterButton>
      </FilterBar>

      <TxTable>
        <caption>
          {confirmedOnly
            ? `Confirmed transactions (${visibleTransactions.length} of ${confirmedCount.toLocaleString()} shown)`
            : minBtc > 0
            ? `Transactions over ${minBtc} BTC (${visibleTransactions.length} of the last ${transactions.length})`
            : movedOnly
            ? "Transfers (last 100 shown) — transactions that moved nothing are hidden"
            : "Transactions (last 100 shown)"}
        </caption>
        <thead>
          <tr>
            <DesktopTh>Time</DesktopTh>
            <th>Hash</th>
            <th>In</th>
            <th>Out</th>
            <th>Moved</th>
          </tr>
        </thead>
        <tbody>
          {/* See https://en.bitcoin.it/wiki/Transaction */}
          {visibleTransactions.length === 0 && (
            <tr>
              <EmptyTd colSpan={5}>
                {confirmedOnly
                  ? "None of the transactions on this page have been confirmed yet. It takes a block, which arrives every ten minutes or so."
                  : minBtc > 0
                  ? `Nothing over ${minBtc} BTC in the last ${transactions.length.toLocaleString()} transactions. Most are far smaller than this.`
                  : `None of the last ${transactions.length.toLocaleString()} transactions moved anything — every output went back to an address that paid in. Show them with "Moved something".`}
              </EmptyTd>
            </tr>
          )}
          {visibleTransactions.map(
            ({ time, hash, inputs, out, netMovedBtc, tags, confirmed, confirmedHeight }) => (
              <TxRow key={hash} $tagged={tags.length > 0}>
                <DesktopTd>
                  {time.toLocaleString("en-US", {
                    month: "short", // abbreviated month name
                    day: "2-digit", // two-digit day of the month
                    year: "numeric", // full year
                    hour: "2-digit", // two-digit hours (00-23)
                    minute: "2-digit", // two-digit minutes (00-59)
                    second: "2-digit", // two-digit seconds (00-59)
                    hour12: true, // 12-hour clock format
                  })}
                </DesktopTd>
                <td>
                  <Link to={`/btc-explorer/${hash}`}>{truncateHash(hash)}</Link>
                  {confirmed && (
                    <ConfirmedTag>in #{confirmedHeight.toLocaleString()}</ConfirmedTag>
                  )}
                  {tags.map((tag) => (
                    <WalletTag key={tag.address} to={`/btc-explorer/${tag.address}`}>
                      {tag.label} {tag.direction}
                    </WalletTag>
                  ))}
                </td>
                <td>{inputs.length}</td>
                <td>{out.length}</td>
                <td>{btcToString(netMovedBtc)} BTC</td>
              </TxRow>
            )
          )}
        </tbody>
      </TxTable>

      {/* From routeMeta, because the prerender step writes these same words
          into the served HTML - see the note on the mempool page's copy. */}
      <Faq>
        <FaqTitle>Common questions</FaqTitle>
        {routeMeta["/"].faq.map(({ question, answer }) => (
          <div key={question}>
            <Question>{question}</Question>
            <Answer>{answer}</Answer>
          </div>
        ))}
      </Faq>
    </BtcLivePage>
  );
};

export default BtcLive;
